/**
 * Data Access Layer for Staff Availability
 *
 * Availability windows per person. program_id is optional: a null window is
 * general availability, a set window scopes availability to one programme.
 */

import { getClient } from './client.js';
import { TABLES } from './tables.js';
import { getCurrentAccountId } from './account-context.js';
import type { StaffAvailability, StaffAvailabilityCreate } from './types.js';

export async function listAvailability(filters?: {
  personId?: string;
  programId?: string | null;
}): Promise<StaffAvailability[]> {
  let query = getClient().from(TABLES.staff_availability).select('*');

  const accountId = getCurrentAccountId();
  if (accountId) query = query.eq('account_id', accountId);

  if (filters?.personId) query = query.eq('person_id', filters.personId);
  if (filters?.programId !== undefined) {
    if (filters.programId === null) query = query.is('program_id', null);
    else query = query.eq('program_id', filters.programId);
  }

  const { data, error } = await query.order('start_time');
  if (error) throw error;
  return (data || []) as StaffAvailability[];
}

export async function createAvailability(
  input: StaffAvailabilityCreate,
): Promise<StaffAvailability> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('createAvailability: no account context set');

  const { data, error } = await getClient()
    .from(TABLES.staff_availability)
    .insert({
      account_id: accountId,
      person_id: input.person_id,
      program_id: input.program_id ?? null,
      start_time: input.start_time,
      end_time: input.end_time,
    })
    .select()
    .single();

  if (error) throw error;
  return data as StaffAvailability;
}

export async function deleteAvailability(id: string): Promise<void> {
  const { error } = await getClient().from(TABLES.staff_availability).delete().eq('id', id);
  if (error) throw error;
}

/**
 * Replace a person's availability windows for a given programme scope in one go.
 * Pass programId = null to replace their general (unscoped) availability.
 */
export async function setAvailability(
  personId: string,
  windows: Array<{ start_time: string; end_time: string }>,
  options?: { programId?: string | null },
): Promise<void> {
  const accountId = getCurrentAccountId();
  if (!accountId) throw new Error('setAvailability: no account context set');

  const programId = options?.programId ?? null;
  const client = getClient();

  let deleteQuery = client
    .from(TABLES.staff_availability)
    .delete()
    .eq('person_id', personId);
  deleteQuery = programId === null
    ? deleteQuery.is('program_id', null)
    : deleteQuery.eq('program_id', programId);

  const { error: deleteError } = await deleteQuery;
  if (deleteError) throw deleteError;

  if (windows.length > 0) {
    const rows = windows.map((w) => ({
      account_id: accountId,
      person_id: personId,
      program_id: programId,
      start_time: w.start_time,
      end_time: w.end_time,
    }));
    const { error: insertError } = await client.from(TABLES.staff_availability).insert(rows);
    if (insertError) throw insertError;
  }
}
